import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

import BaseAvatar from "@/components_v2/Avatar/BaseAvatar";
import BaseStatusChip from "@/components_v2/StatusChip/BaseStatusChip";
import { STATUS_CHIP_VARIANTS } from "@/components_v2/StatusChip/constants";
import BaseTag from "@/components_v2/Tags/BaseTag";
import { userProfilePath } from "@/domains/users/routes";
import { getInitials } from "@/domains/certifications/utils";
import CaseActions from "@/features/community/moderation/components/CaseActions";
import CaseBody from "@/features/community/moderation/components/CaseBody";
import DecidedNote from "@/features/community/moderation/components/DecidedNote";
import { STATE_CHIPS } from "@/features/community/moderation/constants";
import {
  getCaseKind,
  getVerdictKey,
  isEdited,
} from "@/features/community/moderation/utils";
import { CaseCardProps } from "@/features/community/moderation/types";
import { MODERATION_STATES } from "@/domains/moderation/constants";

import "../styles.css";

const PERCENT = 100;

const CaseCard = ({ moderationCase, certificationWeek }: CaseCardProps) => {
  const { t } = useTranslation();
  const { author, discussion, aiConfidence, aiRationale } = moderationCase;
  const name = author?.fullName.trim() || author?.email || "";
  const firstName = author?.firstName.trim() || name;
  const topic = discussion?.topics[0];
  const kind = getCaseKind(moderationCase);
  const stateChip = STATE_CHIPS[moderationCase.state];
  const isWaiting = moderationCase.state === MODERATION_STATES.PENDING_HUMAN;
  const isDecided =
    moderationCase.state === MODERATION_STATES.APPROVED ||
    moderationCase.state === MODERATION_STATES.REJECTED;

  return (
    <article className="moderation-card flex flex-col">
      <header className="moderation-card-header flex flex-wrap items-center justify-between gap-200">
        <div className="flex items-center gap-200">
          <BaseAvatar
            size="small"
            className="moderation-avatar"
            label={getInitials(name)}
            imageUrl={author?.profilePicture}
            aria-hidden
          />

          <div className="flex flex-col">
            {author ? (
              <Link
                to={userProfilePath(author.id)}
                className="moderation-author body-s-medium"
              >
                {name}
              </Link>
            ) : (
              <span className="moderation-author body-s-medium">
                {t("moderation.case.unknownAuthor")}
              </span>
            )}

            {topic && (
              <span className="moderation-muted body-s-regular">
                {topic.title}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-200">
          {stateChip && (
            <BaseStatusChip
              label={t(stateChip.labelKey)}
              variant={stateChip.variant}
            />
          )}

          <BaseTag size="small" label={t(`moderation.case.kind.${kind}`)} />

          {certificationWeek !== null && (
            <BaseStatusChip
              label={t("moderation.case.certificationWeek", {
                week: certificationWeek,
              })}
              variant={STATUS_CHIP_VARIANTS.BLUE_TINT}
            />
          )}

          <span className="moderation-muted body-xs-regular">
            {DateTime.fromISO(moderationCase.createdAt).toRelative()}
            {isEdited(moderationCase) && ` · ${t("moderation.case.edited")}`}
          </span>
        </div>
      </header>

      <CaseBody moderationCase={moderationCase} />

      <footer className="moderation-card-footer flex flex-col gap-300">
        <div className="flex flex-col gap-100">
          <div className="flex flex-wrap items-center gap-200">
            <span className="moderation-eyebrow body-xs-regular">
              {t("moderation.case.aiCheck")}
            </span>

            <BaseStatusChip
              label={t(getVerdictKey(moderationCase))}
              variant={STATUS_CHIP_VARIANTS.AMBER}
            />

            {aiConfidence !== null && (
              <span className="moderation-muted body-xs-regular">
                {t("moderation.case.confidence", {
                  percent: Math.round(aiConfidence * PERCENT),
                })}
              </span>
            )}

            <span className="moderation-muted body-xs-regular">
              {t("moderation.case.adminsOnly")}
            </span>
          </div>

          {aiRationale && (
            <p className="moderation-note body-s-regular">{aiRationale}</p>
          )}
        </div>

        {certificationWeek !== null && isWaiting && (
          <p className="moderation-certification-note body-s-regular">
            {t("moderation.case.certificationNote", { name: firstName })}
          </p>
        )}

        {isWaiting && <CaseActions moderationCase={moderationCase} />}

        {isDecided && <DecidedNote moderationCase={moderationCase} />}
      </footer>
    </article>
  );
};

export default CaseCard;
